/**
 * sl-9qm.4.2: TableView Component
 *
 * Database table with sortable headers and configurable columns.
 */
import { createElement, useState } from 'react';
import { z } from 'zod';

import { VIEW_COMPONENT_TYPES } from '../../../constants/view-component-types';
import type {
  ViewComponent,
  ViewComponentRenderProps,
} from '../../../types/view-component.types';
import * as styles from '../styles.css';
import { getContentCopy } from './content-copy';

const configSchema = z.object({
  visibleColumns: z.array(z.string()).default([]),
  striped: z.boolean().default(true),
  compact: z.boolean().default(false),
});

function TableViewRenderer({
  config,
  data,
  dataBindingConfig,
  mode,
  runtimeContext,
}: ViewComponentRenderProps) {
  const { visibleColumns, striped, compact } = config as z.infer<
    typeof configSchema
  >;
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  // Determine columns from data or config
  const columns =
    visibleColumns.length > 0
      ? visibleColumns
      : data.length > 0
        ? Object.keys(data[0])
        : [];

  // Sort data
  let sortedData = data;
  if (sortColumn) {
    sortedData = [...data].sort((a, b) => {
      const aVal = String(a[sortColumn] ?? '');
      const bVal = String(b[sortColumn] ?? '');
      return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }

  const handleSort = (col: string) => {
    if (sortColumn === col) {
      setSortAsc(!sortAsc);
    } else {
      setSortColumn(col);
      setSortAsc(true);
    }
  };

  if (columns.length === 0) {
    const emptyMessage = dataBindingConfig?.databaseDocId
      ? getContentCopy(runtimeContext?.locale, 'emptyColumns')
      : mode === 'builder'
        ? getContentCopy(runtimeContext?.locale, 'notBound')
        : getContentCopy(runtimeContext?.locale, 'unavailable');

    return createElement(
      'div',
      {
        style: {
          padding: 'var(--sl-custom-app-padding-lg)',
          color: 'var(--streamlain-text-secondary-color)',
          textAlign: 'center' as const,
        },
      },
      emptyMessage
    );
  }

  return createElement(
    'table',
    { className: styles.tableView },
    createElement(
      'thead',
      null,
      createElement(
        'tr',
        null,
        columns.map(col =>
          createElement(
            'th',
            {
              key: col,
              className: styles.tableHeader,
              'data-sl-field': col,
              onClick: () => handleSort(col),
              style: compact
                ? {
                    padding:
                      'var(--sl-custom-app-gap-xxs) var(--sl-custom-app-padding-sm)',
                  }
                : undefined,
            },
            col,
            sortColumn === col && (sortAsc ? ' ↑' : ' ↓')
          )
        )
      )
    ),
    createElement(
      'tbody',
      null,
      sortedData.map((row, i) =>
        createElement(
          'tr',
          {
            key: i,
            className: striped ? styles.tableRowStriped : undefined,
          },
          columns.map(col =>
            createElement(
              'td',
              {
                key: col,
                className: styles.tableCell,
                'data-sl-field': col,
                style: compact
                  ? {
                      padding:
                        'var(--sl-custom-app-gap-xxs) var(--sl-custom-app-padding-sm)',
                    }
                  : undefined,
              },
              String(row[col] ?? '')
            )
          )
        )
      )
    )
  );
}

export const TableViewComponent: ViewComponent = {
  id: VIEW_COMPONENT_TYPES.TABLE_VIEW,
  type: 'content',
  name: 'Table View',
  icon: createElement(
    'span',
    { style: { fontSize: 'var(--sl-custom-app-font-size-sm)' } },
    '▤'
  ),
  capabilities: [
    {
      role: 'primary',
      description: 'Table data rows',
      required: true,
      requirements: [],
    },
  ],
  configSchema,
  defaultConfig: {
    visibleColumns: [],
    striped: true,
    compact: false,
  },
  render(props) {
    return createElement(TableViewRenderer, props);
  },
  palettePreview() {
    return createElement(
      'div',
      { className: styles.palettePreview },
      '▤ Table'
    );
  },
};
